import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';

import Button from '@strava/ui/Button';
import cx from '@strava/ui/clsx';
import { camelCase } from 'lodash-es';
import FacebookLogo from '@strava/icons/LogosFacebookXsmall';
import { trackV2 } from 'utils/analytics';
import { useInView } from 'react-intersection-observer';
import styles from './InviteFriendsButton.scss';
import { t } from '../../utils/invite-friends-utils';
import InviteFriendsModal from '../InviteFriendsModal/InviteFriendsModal';
import InviteFriendsContext from '../../state/InviteFriendsContext';
import { reducer } from '../../state/InviteFriends.reducer';
import { OPEN_MODAL } from '../../state/InviteFriends.actions';

const InviteFriendsButton = ({
  source,
  googleClientId,
  currentAthlete,
  locale,
  fbAppId,
  fbVersion,
  mocked,
  defaultTabIndex
}) => {
  const [intersectionObserverRef, inView] = useInView({
    threshold: 1
  });

  const track = useCallback(
    (fields) => {
      trackV2({
        page: 'find_and_invite',
        category: 'connections',
        ...fields,
        properties: {
          source,
          default_tab: defaultTabIndex === 0 ? 'email' : 'facebook',
          ...fields.properties
        }
      });
    },
    [defaultTabIndex, source]
  );

  const [state, dispatch] = useReducer(reducer, {
    isModalOpen: false,
    googleClientId,
    currentAthlete,
    locale,
    fbAppId,
    fbVersion,
    fbFriends: {},
    invitedEmailsList: [],
    mocked,
    emails: '',
    alert: {},
    modalTabIndex: defaultTabIndex
  });

  const providerState = useMemo(
    () => ({
      state,
      dispatch,
      track
    }),
    [state, track]
  );

  const element = 'invite_friends_btn';

  useEffect(() => {
    if (inView) {
      track({ page: source, element, action: 'screen_enter' });
    }
  }, [inView, source, track]);

  const config = (() => {
    let cta = t('cta.find_and_invite_friends');
    let variant = 'text';
    let logo = null;

    if (source === 'onboarding') {
      variant = 'primary';
      cta = t('cta.connect_with_facebook');
      logo = <FacebookLogo />;
    } else if (
      [
        'athlete_search',
        'follows_following',
        'follows_suggested',
        'follows_followers',
        'follows_blocked'
      ].includes(source)
    ) {
      variant = 'primary';
    } else if (source === 'header_menu') {
      cta = t('cta.find_friends');
    }
    return { cta, variant, logo };
  })();

  const handleButtonClick = () => {
    dispatch({ type: OPEN_MODAL });
    track({ page: source, element, action: 'click' }); // TODO confirm what page should be here
  };

  return (
    <InviteFriendsContext.Provider value={providerState}>
      <Button
        type="button"
        onClick={handleButtonClick}
        variant={config.variant}
        data-cy={`invite-friends-btn-${source}`}
        className={cx(styles[camelCase(source)])}
        ref={intersectionObserverRef}
      >
        {config.logo}
        {config.cta}
      </Button>

      {state.isModalOpen && <InviteFriendsModal />}
    </InviteFriendsContext.Provider>
  );
};

InviteFriendsButton.defaultProps = {
  mocked: false,
  defaultTabIndex: 0 // defaults to first tab (email)
};

InviteFriendsButton.propTypes = {
  source: PropTypes.oneOf([
    'athlete_search',
    'dashboard_sidebar',
    'follows_blocked',
    'follows_followers',
    'follows_following',
    'follows_suggested',
    'header_menu',
    'onboarding' // facebook button on onboarding dashboard feed
  ]).isRequired,
  googleClientId: PropTypes.string.isRequired,
  mocked: PropTypes.bool,
  fbAppId: PropTypes.number.isRequired,
  fbVersion: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  currentAthlete: PropTypes.shape({
    email: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired
  }).isRequired,
  defaultTabIndex: PropTypes.oneOf([0, 1]) // 0 - email, 1 - FB
};

export default InviteFriendsButton;
